/**
 * Chat file-attachment primitives, shared by the operator **Ask** page and the portal
 * **ChatAssistant** widget (ADR-0033 file-uploads, slice 5). A small paperclip button +
 * a read-only chip row: the user attaches files to the conversation's sandbox workspace,
 * and the assistant reads them with its `read_uploaded_file` / `code_execution` tools.
 *
 * The chips are INDICATORS, not removable selections — there's no delete-file route; an
 * uploaded file lives in the workspace for the rest of the conversation (reopening the
 * conversation re-loads them via the GET files endpoint). Each surface owns its own upload
 * wiring (different api clients); these are the presentational pieces they share.
 */
import * as React from "react";
import { Eye, EyeOff, FileImage, FileText, Loader2, Paperclip, Sparkles } from "lucide-react";

export interface AttachedFile {
  /** Workspace path, e.g. `uploads/report.pdf` — display strips the directory. */
  name: string;
  /** Object URL for an inline image thumbnail (client-side, current session only —
   *  reopened conversations have no bytes, so they fall back to the file chip). */
  previewUrl?: string;
}

/** `uploads/report.pdf` → `report.pdf` (the chip shows the bare filename). */
export function attachmentBasename(path: string): string {
  const parts = path.split("/");
  return parts[parts.length - 1] || path;
}

/** A browser object URL for an image preview, or `undefined` for a non-image (→ chip).
 *  Guards `createObjectURL` (absent in jsdom) so callers can use it unconditionally. */
export function imagePreviewUrl(file: File): string | undefined {
  if (!file.type?.startsWith("image/")) return undefined;
  if (typeof URL === "undefined" || typeof URL.createObjectURL !== "function") return undefined;
  return URL.createObjectURL(file);
}

export function AttachButton({
  onPick,
  uploading = false,
  disabled = false,
  label,
}: {
  onPick: (files: File[]) => void;
  uploading?: boolean;
  disabled?: boolean;
  label: string;
}) {
  const ref = React.useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={ref}
        type="file"
        multiple
        className="hidden"
        data-testid="chat-file-input"
        onChange={(e) => {
          const picked = Array.from(e.target.files ?? []);
          e.target.value = ""; // allow re-picking the same file
          if (picked.length) onPick(picked);
        }}
      />
      <button
        type="button"
        aria-label={label}
        title={label}
        disabled={disabled || uploading}
        onClick={() => ref.current?.click()}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-elevated hover:text-foreground disabled:opacity-40"
      >
        {uploading ? (
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
        ) : (
          <Paperclip className="h-4 w-4" strokeWidth={1.5} />
        )}
      </button>
    </>
  );
}

/** What the resolved model accepts natively (ADR-0034) — drives the vision badge. */
export interface ModelVision {
  model: string | null;
  provider: string | null;
  /** Subset of `["image","pdf"]`; empty ⇒ text-only. */
  vision: string[];
  configured: boolean;
  /** Whether the resolved assistant can READ uploaded files (the bound agent has the
   *  `uploads`/`code_exec` capability). False for the system assistant ⇒ the chat hides the
   *  attach button (no capability, no upload affordance). Optional so an Ask-shaped caller
   *  that never sets it defaults to hidden. */
  uploads?: boolean;
  /** Operator-plane cost context (Ask only; the portal omits it). */
  pricing?: { input_per_mtok: number; output_per_mtok: number } | null;
  budget?: { monthly_usd: number; spent_usd: number } | null;
}

/** Live-session stats shown in the popover (Ask passes it; the portal may pass a subset). */
export interface SessionStats {
  /** Messages exchanged this session (user + assistant turns). */
  messages: number;
  /** Estimated USD spent since the page opened (accumulated from `usage` events). */
  costUsd?: number;
  /** Files uploaded into the conversation workspace. */
  files?: number;
}

const _VISION_LABEL: Record<string, string> = { image: "Immagini", pdf: "PDF" };

/** A compact USD string — sub-cent turns are common, so 4 decimals under $1, else 2. */
function fmtUsd(n: number): string {
  return n < 1 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`;
}

/**
 * A small clickable badge near the composer that tells the user what the CURRENT model can
 * SEE — images / PDF natively, or text-only (other files go through code). The capability is
 * DATA (the LLM config's declared modalities, ADR-0034), so this reflects whatever the
 * operator configured — no deploy. Deliberately a tiny popover so we can extend it later
 * (cost, context window, …) without a redesign.
 */
export function ModelVisionBadge({
  caps,
  session,
  iconOnly = false,
}: {
  caps: ModelVision | null;
  session?: SessionStats | null;
  /** Render just the eye icon (a tiny round button to tuck inside a composer pill)
   *  instead of the icon + text-label badge. The popover then opens leftward. */
  iconOnly?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    // Capture phase so a click anywhere closes it even inside stop-propagation handlers.
    window.addEventListener("mousedown", onClick, true);
    return () => window.removeEventListener("mousedown", onClick, true);
  }, [open]);
  if (!caps) return null;
  const sees = caps.vision.filter((c) => c in _VISION_LABEL);
  const isVision = sees.length > 0;
  const summary = isVision ? sees.map((c) => _VISION_LABEL[c]).join(" + ") : "Solo testo";
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="Capacità del modello"
        title="Cosa vede il modello"
        onClick={() => setOpen((v) => !v)}
        className={
          iconOnly
            ? "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-muted-foreground transition hover:bg-elevated hover:text-foreground"
            : "flex h-7 items-center gap-1 rounded-full border border-border px-2 text-[11px] text-muted-foreground transition hover:bg-elevated hover:text-foreground"
        }
      >
        {iconOnly ? (
          // The pill glyph reads as "AI model capabilities" as a whole; the popover
          // reveals the vision/text detail on click.
          <Sparkles className="h-4 w-4" strokeWidth={1.5} />
        ) : isVision ? (
          <Eye className="h-3 w-3" strokeWidth={1.5} />
        ) : (
          <EyeOff className="h-3 w-3" strokeWidth={1.5} />
        )}
        {!iconOnly && <span className="hidden sm:inline">{summary}</span>}
      </button>
      {open && (
        // The badge form sits at the right of the composer → open leftward (right-0).
        // The icon-only form is tucked at the LEFT of the pill → open rightward (left-0).
        <div
          className={`absolute bottom-9 z-20 w-64 max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-surface p-3 text-xs shadow-lg ${
            iconOnly ? "left-0" : "right-0"
          }`}
        >
          <div className="mb-1 font-medium text-foreground">
            {caps.model ?? "Modello di test"}
          </div>
          {caps.provider && (
            <div className="mb-2 text-[11px] uppercase tracking-wide text-muted-foreground">
              {caps.provider}
            </div>
          )}
          <div className="flex items-start gap-2">
            <FileImage className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" strokeWidth={1.5} />
            <div className="text-muted-foreground">
              {isVision ? (
                <>
                  Legge <span className="text-foreground">{summary}</span> nativamente. Altri
                  formati vengono letti via codice.
                </>
              ) : (
                <>
                  Questo modello legge <span className="text-foreground">solo testo</span>. Immagini
                  e PDF vengono letti via codice (estrazione), non visti direttamente.
                </>
              )}
            </div>
          </div>
          {!caps.configured && (
            <div className="mt-2 text-[11px] text-warning">
              Nessun provider configurato — modello di test.
            </div>
          )}

          {/* Costi (operator-plane: solo Ask passa pricing/budget) */}
          {(caps.pricing || caps.budget) && (
            <div className="mt-3 border-t border-border/60 pt-2">
              <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/80">
                Costi
              </div>
              {caps.pricing && (
                <div className="flex justify-between text-muted-foreground">
                  <span>Prezzo (stima)</span>
                  <span className="text-foreground">
                    {fmtUsd(caps.pricing.input_per_mtok)} / {fmtUsd(caps.pricing.output_per_mtok)} · Mtok
                  </span>
                </div>
              )}
              {caps.budget && (
                <div className="mt-0.5 flex justify-between text-muted-foreground">
                  <span>Budget mensile</span>
                  <span className="text-foreground">
                    {fmtUsd(caps.budget.spent_usd)} / {fmtUsd(caps.budget.monthly_usd)}
                    {caps.budget.monthly_usd > 0 && (
                      <span className="text-muted-foreground">
                        {" "}
                        ({Math.round((caps.budget.spent_usd / caps.budget.monthly_usd) * 100)}%)
                      </span>
                    )}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Sessione corrente */}
          {session && (
            <div className="mt-3 border-t border-border/60 pt-2">
              <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground/80">
                Sessione corrente
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Messaggi</span>
                <span className="text-foreground">{session.messages}</span>
              </div>
              {typeof session.costUsd === "number" && (
                <div className="mt-0.5 flex justify-between text-muted-foreground">
                  <span>Costo stimato</span>
                  <span className="text-foreground">{fmtUsd(session.costUsd)}</span>
                </div>
              )}
              {typeof session.files === "number" && session.files > 0 && (
                <div className="mt-0.5 flex justify-between text-muted-foreground">
                  <span>File caricati</span>
                  <span className="text-foreground">{session.files}</span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function AttachmentChips({ files }: { files: AttachedFile[] }) {
  if (!files.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {files.map((f) =>
        f.previewUrl ? (
          // Image → inline thumbnail (the bytes live only in this browser session).
          <img
            key={f.name}
            src={f.previewUrl}
            alt={attachmentBasename(f.name)}
            title={attachmentBasename(f.name)}
            className="h-16 w-16 rounded-lg border border-border object-cover"
          />
        ) : (
          <span
            key={f.name}
            title={attachmentBasename(f.name)}
            className="inline-flex max-w-[12rem] items-center gap-1 rounded-full border border-border bg-elevated px-2 py-0.5 text-xs text-foreground"
          >
            <FileText className="h-3 w-3 shrink-0 text-muted-foreground" strokeWidth={1.5} />
            <span className="truncate">{attachmentBasename(f.name)}</span>
          </span>
        ),
      )}
    </div>
  );
}
